/**
 * Pointer-event-based draggability for a fixed-positioned overlay element.
 *
 * Used for the mocap webcam preview so users can drag it out of the way of the
 * rig viewport. Position persists across mocap toggles and page reloads via
 * localStorage. On restore, the position is clamped into the current viewport
 * so a window-shrink can't strand the panel offscreen.
 *
 * Implementation notes:
 *  - Pointer Events (vs MouseEvents) handle touch and pen for free.
 *  - `setPointerCapture` ensures we keep getting move/up events even if the
 *    pointer leaves the element (e.g. moves over the canvas).
 *  - The element starts CSS-positioned via `top` + `right`. On first drag we
 *    snap to absolute `top` + `left` and clear `right` so we have a single
 *    coordinate system for all subsequent drags.
 */
export class DraggablePanel {
  private offset_x: number = 0
  private offset_y: number = 0
  private dragging: boolean = false
  private active_pointer_id: number = -1
  private readonly storage_key: string

  constructor (private readonly element: HTMLElement, storage_key: string) {
    this.storage_key = storage_key
    element.addEventListener('pointerdown', this.on_pointer_down)
    element.addEventListener('pointermove', this.on_pointer_move)
    element.addEventListener('pointerup', this.on_pointer_up)
    element.addEventListener('pointercancel', this.on_pointer_up)
    this.restore_saved_position()
  }

  private on_pointer_down = (e: PointerEvent): void => {
    // Only respond to primary button on mouse; touch and pen always pass.
    if (e.pointerType === 'mouse' && e.button !== 0) return

    try {
      this.element.setPointerCapture(e.pointerId)
    } catch (_) { /* element not attached yet — fall through, listener still works */ }
    this.active_pointer_id = e.pointerId
    this.dragging = true

    // Snap from CSS top+right anchoring to absolute top+left at this instant so
    // the cursor offset math works in a single coordinate system.
    const rect = this.element.getBoundingClientRect()
    this.element.style.left = `${rect.left}px`
    this.element.style.top = `${rect.top}px`
    this.element.style.right = ''

    this.offset_x = e.clientX - rect.left
    this.offset_y = e.clientY - rect.top
    this.element.classList.add('dragging')
    e.preventDefault()
  }

  private on_pointer_move = (e: PointerEvent): void => {
    if (!this.dragging || e.pointerId !== this.active_pointer_id) return
    const new_left = e.clientX - this.offset_x
    const new_top = e.clientY - this.offset_y
    const max_left = Math.max(0, window.innerWidth - this.element.offsetWidth)
    const max_top = Math.max(0, window.innerHeight - this.element.offsetHeight)
    const clamped_left = Math.max(0, Math.min(new_left, max_left))
    const clamped_top = Math.max(0, Math.min(new_top, max_top))
    this.element.style.left = `${clamped_left}px`
    this.element.style.top = `${clamped_top}px`
  }

  private on_pointer_up = (e: PointerEvent): void => {
    if (e.pointerId !== this.active_pointer_id) return
    this.dragging = false
    this.active_pointer_id = -1
    this.element.classList.remove('dragging')
    try {
      this.element.releasePointerCapture(e.pointerId)
    } catch (_) { /* already released or never had capture */ }
    this.save_position()
  }

  private save_position (): void {
    const left = parseFloat(this.element.style.left)
    const top = parseFloat(this.element.style.top)
    if (Number.isNaN(left) || Number.isNaN(top)) return
    try {
      localStorage.setItem(this.storage_key, JSON.stringify({ left, top }))
    } catch (_) { /* private mode, full quota, etc. — non-fatal */ }
  }

  private restore_saved_position (): void {
    let raw: string | null
    try {
      raw = localStorage.getItem(this.storage_key)
    } catch (_) { return }
    if (raw === null) return

    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch (_) { return }
    if (typeof parsed !== 'object' || parsed === null) return

    const { left, top } = parsed as { left?: unknown, top?: unknown }
    if (typeof left !== 'number' || typeof top !== 'number') return

    // Clamp to viewport. The element may not have rendered yet so we can't read
    // its real dimensions; use the CSS-declared default size as a safe fallback.
    const fallback_w = 240
    const fallback_h = 180
    const max_left = Math.max(0, window.innerWidth - fallback_w)
    const max_top = Math.max(0, window.innerHeight - fallback_h)
    const clamped_left = Math.max(0, Math.min(left, max_left))
    const clamped_top = Math.max(0, Math.min(top, max_top))

    this.element.style.left = `${clamped_left}px`
    this.element.style.top = `${clamped_top}px`
    this.element.style.right = ''
  }
}
